import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { UtilitiesService } from '../../../../services/utilities.service';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { BrandFilterService } from '../../../../services/filters/brand-filter.service';
import { PhotographerStatusFilterService } from '../../../../services/filters/photographer-status-filter.service';
import { SnackbarType } from '../../../../shared/components/snackbar/snackbar.component';
import { FilterListComponent } from '../../../../shared/components/filter-list/filter-list.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { OrderQueries, PhotographerQueries } from '../../../../shared/types/get-query';
import { MutationQuery } from '../../../../shared/types/mutation-query.enum';
import { Selectable } from '../../../../shared/types/selectable.type';
import { Brand } from '../../models/new/brand.model';
import { Status } from '../../models/new/status';
import { Photographer, PhotographerValidation } from '../../models/new/photographer';
import { PhotographerDashboardResume } from '../../models/new/photographerDashboardResume';
import { PhotographerMapper } from '../../services/photographer-mapper';
import { ResumeMapper } from '../../services/resume-mapper';
import { OrderStatus } from '../../models/new/order';

@Component({
	standalone: false,
	selector: 'bt-company-photographer-detail',
	templateUrl: './company-photographer-detail.component.html',
	styleUrls: ['./company-photographer-detail.component.scss']
})
export class CompanyPhotographerDetailComponent {
	photographer?: Photographer;
	photographerValidation: PhotographerValidation = new PhotographerValidation();
	loading = false;
	isNew = false;
	orderStatus = OrderStatus;
	photographerDashboardResume: PhotographerDashboardResume = new PhotographerDashboardResume();

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private graphQlService: GraphQLService,
		private utilitiesService: UtilitiesService,
		private photographerMapper: PhotographerMapper,
		private resumeMapper: ResumeMapper,
		private brandFilterService: BrandFilterService,
		private photographerStatusFilterService: PhotographerStatusFilterService
	) {}

	ngOnInit() {
		this.getPhotographer();
	}

	getPhotographer() {
		const photographerIdParam = this.route.snapshot.paramMap.get('photographerId');

		// Modalità creazione: /company/photographer-detail/new
		if (photographerIdParam === 'new') {
			this.isNew = true;
			this.photographer = new Photographer({});
			this.photographerDashboardResume = new PhotographerDashboardResume();
			return;
		}

		const photographerId = Number(photographerIdParam);

		if (!photographerId) {
			this.utilitiesService.showSnackbar('Invalid photographer id', SnackbarType.Error);
			this.router.navigate(['/company/photographer-list']);
			return;
		}

		this.loading = true;

		this.graphQlService
			.getData<any>(PhotographerQueries.GET_PHOTOGRAPHERS, {
				where: {
					personId: {
						in: [photographerId]
					}
				},
				first: 1
			})
			.pipe(
				map((res) => {
					const node = res.data.photographers.nodes[0];
					return node ? this.photographerMapper.toPhotographer(node) : undefined;
				})
			)
			.subscribe({
				next: (photographer) => {
					this.photographer = photographer;
					this.getOrders();

					this.loading = false;

					if (!photographer) {
						this.utilitiesService.showSnackbar('Photographer not found', SnackbarType.Error);
					}
				},
				error: (err) => {
					this.loading = false;
					this.utilitiesService.showSnackbar('Error fetching photographer detail', SnackbarType.Error);
					console.error(err);
				}
			});
	}

	getOrders() {
		const photographerId = this.photographer?.id;

		if (!photographerId) {
			this.photographerDashboardResume = new PhotographerDashboardResume();
			return;
		}

		this.graphQlService.getData<any>(OrderQueries.GET_PHOTOGRAPHER_DASHBOARD_RESUME, { photographerId: [photographerId] }).subscribe({
			next: (res: any) => {
				this.photographerDashboardResume = this.resumeMapper.toPhotographerDashboardResume(res?.data);
			},
			error: (err) => {
				this.photographerDashboardResume = new PhotographerDashboardResume();
				this.utilitiesService.showSnackbar('Error fetching photographer dashboard counters', SnackbarType.Error);
				console.error(err);
			}
		});
	}

	openBrandFilterModal(): void {
		if (!this.photographer) {
			return;
		}

		this.dialog
			.open(FilterListComponent<Brand>, {
				data: {
					title: 'Brands',
					selected: [],
					filterService: this.brandFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe((res?: Array<Selectable<Brand>>) => {
				if (!res || !this.photographer) {
					return;
				}

				this.photographer.brands = res.map((el) => new Brand({ id: Number(el.displayId), name: el.displayName }));
			});
	}

	openStatusModal() {
		if (!this.photographer) {
			return;
		}

		this.dialog
			.open(FilterListComponent<Status>, {
				data: {
					title: 'Statuses',
					singleSelection: true,
					selected: this.photographer.status
						? [
								{
									displayId: this.photographer.status.id,
									displayName: this.photographer.status.name,
									selected: true
								} as Selectable<Status>
							]
						: [],
					filterService: this.photographerStatusFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe((res?: Array<Selectable<Status>>) => {
				if (!res || !res.length || !this.photographer) {
					return;
				}

				const selected = res[0];

				this.photographer.status = new Status({
					id: Number(selected.displayId),
					name: selected.displayName
				});
			});
	} /*

	openCostModal(cost: TypologyCost) {
		this.dialog
			.open(PhotographerCostModalComponent, {
				data: cost,
				width: '400px'
			})
			.afterClosed()
			.subscribe((updatedCost?: TypologyCost) => {
				if (!updatedCost) {
					return;
				}

				cost.typology = updatedCost.typology;
				cost.amount = updatedCost.amount;
			});
	}

	addTypology() {
		if (!this.photographer) {
			return;
		}

		this.photographer.typologyCosts = this.photographer.typologyCosts || [];
		this.photographer.typologyCosts.push(
			new TypologyCost({
				typology: 'New typology',
				amount: 0
			})
		);
	}

	openRemoveTypologyModal(cost: TypologyCost) {
		if (!this.photographer?.typologyCosts) {
			return;
		}

		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Confirm removal',
					message: `Are you sure you want to remove ${cost.typology || 'this typology'}?`
				}
			})
			.afterClosed()
			.subscribe((confirmed: boolean) => {
				if (!confirmed || !this.photographer?.typologyCosts) {
					return;
				}

				this.photographer.typologyCosts = this.photographer.typologyCosts.filter((item) => item !== cost);
			});
	}*/

	goToPhotographerOrders(statusId?: number) {
		const photographerId = this.photographer?.id;

		if (!photographerId) {
			this.utilitiesService.showSnackbar('Photographer id not available', SnackbarType.Error);
			return;
		}

		this.router.navigate(['/company/order-list'], {
			queryParams: {
				photographers: [photographerId],
				statuses: statusId
			}
		});
	}

	validate(): boolean {
		this.photographerValidation.firstName = !!this.photographer?.firstName?.trim();
		this.photographerValidation.lastName = !!this.photographer?.lastName?.trim();

		return this.photographerValidation.firstName && this.photographerValidation.lastName;
	}

	onFirstNameChange(value: string) {
		if (!this.photographer) {
			return;
		}

		this.photographer.firstName = value;
		this.photographerValidation.firstName = true;
	}

	onLastNameChange(value: string) {
		if (!this.photographer) {
			return;
		}

		this.photographer.lastName = value;
		this.photographerValidation.lastName = true;
	}

	saveChanges() {
		if (!this.photographer) {
			return;
		}

		if (!this.validate()) {
			this.utilitiesService.showSnackbar('Please fill required fields', SnackbarType.Error);
			return;
		}

		const firstName = this.photographer.firstName?.trim() || '';
		const lastName = this.photographer.lastName?.trim() || '';

		const upsertPhotographerInput = {
			personId: this.photographer.id ?? null,
			firstName,
			lastName,
			email: this.photographer.email,
			phoneNumber: this.photographer.phoneNumber,
			contractStartDate: this.photographer.contractStartDate ?? null,
			contractEndDate: this.photographer.contractEndDate ?? null,
			statusId: this.photographer.status?.id ?? null,
			brandIds: this.photographer.brands?.map((b) => b.id) ?? []
		};

		this.loading = true;

		this.graphQlService.postData(MutationQuery.UPSERT_PHOTOGRAPHER, { upsertPhotographerInput }).subscribe({
			next: (res: any) => {
				this.loading = false;

				if (!res?.data?.upsertPhotographer?.success) {
					this.utilitiesService.showSnackbar(res?.data?.upsertPhotographer?.message || 'Error saving photographer', SnackbarType.Error);
					return;
				}

				this.utilitiesService.showSnackbar('Photographer changes saved', SnackbarType.Success);

				const savedId = Number(res.data.upsertPhotographer.message);

				if (this.isNew && savedId) {
					// Passo dalla modalità creazione alla scheda del fotografo appena creato
					this.router.navigate(['/company/photographer-detail', savedId]);
				} else {
					this.getPhotographer();
				}
			},
			error: (err) => {
				this.loading = false;
				this.utilitiesService.showSnackbar(err?.networkError?.error?.errors?.[0]?.message || 'Error saving photographer', SnackbarType.Error);
				console.error(err);
			}
		});
	}

	deletePhotographer() {
		if (!this.photographer?.id) {
			return;
		}

		const personId = this.photographer.id;

		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Confirm deletion',
					message: `Are you sure you want to delete photographer ${this.photographer.fullName || ''}?`
				}
			})
			.afterClosed()
			.subscribe((confirmed: boolean) => {
				if (!confirmed) {
					return;
				}

				this.loading = true;

				this.graphQlService.postData(MutationQuery.DELETE_PHOTOGRAPHER, { deletePhotographerInput: { personId } }).subscribe({
					next: (res: any) => {
						this.loading = false;

						if (!res?.data?.deletePhotographer?.success) {
							this.utilitiesService.showSnackbar(res?.data?.deletePhotographer?.message || 'Error deleting photographer', SnackbarType.Error);
							return;
						}

						this.utilitiesService.showSnackbar('Photographer deleted successfully', SnackbarType.Success);
						this.router.navigate(['/company/photographer-list']);
					},
					error: (err) => {
						this.loading = false;
						this.utilitiesService.showSnackbar(err?.networkError?.error?.errors?.[0]?.message || 'Error deleting photographer', SnackbarType.Error);
						console.error(err);
					}
				});
			});
	}
}
